import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { t } from "i18next";
import { countries } from "../../../../enums/countries";
import { useFetch } from "../../../../hooks/useFetch";
import { ProducerById } from "../../../../types/types";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../ui/Button";
import { Separator } from "../../../../ui/Separator";
import DeleteProducer from "../DeleteProducer";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { useAppStore } from "../../../../stores/RootStore";

type Props = {
  id: string | undefined;
  currentProducer: ProducerById | undefined;
};

const ProducerSettingsTab = ({ id, currentProducer }: Props) => {
  const countryId = currentProducer?.relationships.country.data.id;
  const countryLabel = countries.find((country) => country.value === countryId)?.label;

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, control } = useForm({
    defaultValues: {
      name: currentProducer?.attributes.name,
      country: { value: countryId, label: countryLabel },
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  const [{ response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError }, doFetchItemUpdate] =
    useFetch(`/directories/producers/${id}`);

  const producers = useAppStore((store) => store.producers)!;
  const setProducers = useAppStore((store) => store.setProducers);

  const editProducerItem = (itemSaveResponse: { data: unknown }) => {
    const newProducerData = itemSaveResponse.data as unknown as ProducerById;
    const oldProducerData = producers?.find((producer) => producer.id == currentProducer?.id)!;
    const newCountry = countries.find((country) => country.value == newProducerData.relationships.country.data.id)
      ?.label!;
    const newName = newProducerData.attributes.name;
    const newProducer = {
      ...oldProducerData,
      attributes: { ...oldProducerData.attributes, name: newName, country: newCountry },
    };
    const withoutProducer = producers?.filter((producer) => producer.id != currentProducer?.id)!;
    setProducers([...withoutProducer, newProducer]);
  };

  useEffect(() => {
    if (!itemUpdateResponse) return;
    editProducerItem(itemUpdateResponse);
  }, [itemUpdateResponse]);

  const onSubmit = (formData: any) => {
    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: {
          name: formData.name,
          country_id: formData.country,
        },
      },
    });
  };

  return (
    <>
      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ScrollArea>
            <div className="flex flex-col gap-3">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("title_name")}`} />
                  </div>
                )}
              />
              <Controller
                name="country"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("country")}</Label>
                    <SelectButton
                      label={t("choose_country")}
                      searchable={true}
                      options={countries!}
                      value={value ? countries?.find((s) => s.value == value.value)! : null}
                      onChange={(e) => onChange(e?.value)}
                    />
                  </div>
                )}
              />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </form>
      </div>
      <DeleteProducer id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />
    </>
  );
};

export default ProducerSettingsTab;
